import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, PartyPopper, RefreshCcw, ShoppingBag, XCircle } from "lucide-react";
import { API } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentResult() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { clear } = useCart();
  const isSuccess = pathname.includes("success");
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState(isSuccess ? "checking" : "cancelled");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!isSuccess || !sessionId) return;
    let interval;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      setAttempts(tries);
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (res.data.payment_status === "expired" || res.data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch {
        setStatus("error");
        return;
      }
      if (tries >= 20) {
        setStatus("timeout");
        return;
      }
      interval = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isSuccess]);

  return (
    <main className="flex min-h-screen items-center justify-center pb-24 pt-28" data-testid="payment-result-page">
      <div className="mx-auto max-w-md px-5 text-center">
        {status === "checking" && (
          <>
            <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-lav-50">
              <Loader2 className="h-9 w-9 animate-spin text-lav-600" />
            </span>
            <h1 className="mt-6 font-display text-4xl font-semibold text-ink">
              Confirming your payment...
            </h1>
            <p className="mt-3 text-muted" data-testid="payment-checking-note">
              This only takes a moment — checking attempt {attempts + 1}.
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-mint-50">
              <PartyPopper className="h-9 w-9 text-mint-600" />
            </span>
            <h1 className="mt-6 font-display text-4xl font-semibold text-ink" data-testid="payment-success-title">
              Payment received!
            </h1>
            <p className="mt-3 text-muted">
              Your Sparkwear order is confirmed — a receipt is on its way from
              Stripe. We're getting your sparkles packed.
            </p>
            <Link
              to="/shop"
              data-testid="payment-success-shop-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 font-display text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
            >
              Keep Shopping <ArrowRight className="h-5 w-5" />
            </Link>
          </>
        )}

        {status === "cancelled" && (
          <>
            <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-butter-50">
              <XCircle className="h-9 w-9 text-butter-500" />
            </span>
            <h1 className="mt-6 font-display text-4xl font-semibold text-ink">
              Payment cancelled
            </h1>
            <p className="mt-3 text-muted">
              No money has moved. Your bag is safe — try again whenever you're
              ready.
            </p>
            <Link
              to="/checkout"
              data-testid="payment-cancelled-retry-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 font-display text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
            >
              Back to Checkout <ArrowRight className="h-5 w-5" />
            </Link>
          </>
        )}

        {(status === "timeout" || status === "failed" || status === "error") && (
          <>
            <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-blush-50">
              <RefreshCcw className="h-9 w-9 text-blush-600" />
            </span>
            <h1 className="mt-6 font-display text-4xl font-semibold text-ink">
              {status === "failed" ? "Payment didn't go through" : "Still confirming..."}
            </h1>
            <p className="mt-3 text-muted">
              {status === "failed"
                ? "The payment was cancelled or declined — nothing has been charged."
                : "This is taking longer than usual. Refresh to check again, or contact us if it persists."}
            </p>
            <motion.button
              whileHover={{ scale: 1.03 }}
              onClick={() => window.location.reload()}
              data-testid="payment-retry-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 font-display text-base font-semibold text-white"
            >
              <RefreshCcw className="h-5 w-5" /> Check Again
            </motion.button>
          </>
        )}
      </div>
    </main>
  );
}
